import { useEffect, useState } from "react";
import "./FeedbackInbox.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const PAGE_SIZE = 50;
const STATUS_LABELS = {
  new: "New",
  reviewed: "Reviewed",
  resolved: "Resolved",
};

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

async function readResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || `Request failed (${response.status}).`);
  }
  return payload;
}

export default function FeedbackInbox({ previewMode = false }) {
  const [status, setStatus] = useState("new");
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [error, setError] = useState("");
  const [previewNotice, setPreviewNotice] = useState("");

  useEffect(() => {
    if (previewMode) {
      setPreviewNotice("Inbox data is available after signing in to the live service.");
      setLoading(false);
      return undefined;
    }
    setPreviewNotice("");
    let cancelled = false;
    setLoading(true);
    setError("");
    setItems([]);
    setNextToken("");

    fetch(`${API_BASE}/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "list", status, limit: PAGE_SIZE }),
    })
      .then(readResponse)
      .then((payload) => {
        if (cancelled) return;
        setItems(payload.items || []);
        setNextToken(payload.nextToken || "");
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message || "Unable to load feedback.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [previewMode, status]);

  const loadMore = async () => {
    if (!nextToken || loading) return;
    setLoading(true);
    setError("");
    try {
      const payload = await readResponse(await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list", status, limit: PAGE_SIZE, nextToken }),
      }));
      setItems((current) => [...current, ...(payload.items || [])]);
      setNextToken(payload.nextToken || "");
    } catch (requestError) {
      setError(requestError.message || "Unable to load more feedback.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (item, nextStatus) => {
    setUpdatingId(item.feedbackId);
    setError("");
    try {
      await readResponse(await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update", feedbackId: item.feedbackId, status: nextStatus }),
      }));
      setItems((current) => current.filter((entry) => entry.feedbackId !== item.feedbackId));
    } catch (requestError) {
      setError(requestError.message || "Unable to update feedback.");
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <main className="feedback-inbox-page">
      <h1 className="feedback-inbox-sr-only">Feedback Inbox</h1>

      <div className="feedback-inbox-toolbar">
        <div className="feedback-inbox-filter">
          <label htmlFor="feedback-status-filter">Status</label>
          <select id="feedback-status-filter" value={status} onChange={(event) => setStatus(event.target.value)}>
            {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <span className="feedback-inbox-count">{items.length} shown</span>
      </div>

      {previewNotice && <div className="feedback-inbox-preview-note" role="status">{previewNotice}</div>}
      {error && <div className="feedback-inbox-error" role="alert">{error}</div>}
      {loading && items.length === 0 && <div className="feedback-inbox-empty">Loading feedback…</div>}
      {!loading && items.length === 0 && !error && <div className="feedback-inbox-empty">No {STATUS_LABELS[status].toLowerCase()} feedback.</div>}

      <div className="feedback-inbox-list">
        {items.map((item) => {
          const nextStatus = item.status === "new" ? "reviewed" : item.status === "reviewed" ? "resolved" : "new";
          const actionLabel = nextStatus === "reviewed" ? "Mark reviewed" : nextStatus === "resolved" ? "Mark resolved" : "Reopen";
          return (
            <article className="feedback-inbox-item" key={item.feedbackId}>
              <div className="feedback-inbox-item-header">
                <span className={`feedback-inbox-status feedback-inbox-status-${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span>
                <time dateTime={item.createdAt}>{formatDate(item.createdAt)}</time>
              </div>
              <p className="feedback-inbox-message">{item.message}</p>
              <div className="feedback-inbox-meta">
                <span>From {item.submittedBy || "Unknown user"}</span>
                {item.page && <span>{item.page}</span>}
                {item.updatedBy && <span>Updated by {item.updatedBy}</span>}
              </div>
              <div className="feedback-inbox-item-actions">
                <button className="cug-reset-btn" type="button" disabled={updatingId === item.feedbackId} onClick={() => updateStatus(item, nextStatus)}>
                  {updatingId === item.feedbackId ? "Updating…" : actionLabel}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {nextToken && <div className="feedback-inbox-load-more">
        <button className="compare-btn" type="button" disabled={loading} onClick={loadMore}>{loading ? "Loading…" : "Load more"}</button>
      </div>}
    </main>
  );
}
